import React from 'react';

import {
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControl,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { DisplayFilters } from 'src/utils/filters/display-filters';
import _ from 'lodash';
import { TagCategory } from '../../services/panacea/types';
import { toggleTag } from 'src/utils/filters/selected-filters';
import StyledFilledSelect from './StyledFilledSelect';

interface Props {
  displayFilters: DisplayFilters;
  selectedFilters: DisplayFilters;
  setSelectedFilters: React.Dispatch<React.SetStateAction<DisplayFilters>>;
}

const SHELF_LIFE_TAG_CATEGORY_LOGICAL_ID = 'SHELF_LIFE';

const ShelfLifeFilter: React.FC<Props> = ({
  displayFilters,
  selectedFilters,
  setSelectedFilters,
}) => {
  const theme = useTheme();

  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );

  const [dropdownIsOpen, setDropdownIsOpen] = React.useState(false);

  const displayTagsByCategory = _.keyBy(
    displayFilters.tagCategories,
    (category) => category.logicalId,
  );
  const shelfLifeDisplayTagCategory =
    displayTagsByCategory[SHELF_LIFE_TAG_CATEGORY_LOGICAL_ID];

  if (!shelfLifeDisplayTagCategory) {
    // This might occur on an initial render
    return null;
  }

  if (shelfLifeDisplayTagCategory?.tags?.length < 2) {
    // Do not display the filter if there are fewer than two options, as that filter will not do anything.
    return null;
  }

  const selectedTagsByCategory = _.keyBy(
    selectedFilters.tagCategories,
    (category) => category.logicalId,
  );
  const selectedShelfLifeTagCategory =
    selectedTagsByCategory[SHELF_LIFE_TAG_CATEGORY_LOGICAL_ID];

  const currentSelectedIds =
    selectedShelfLifeTagCategory?.tags?.map((t) => t.id) ?? [];

  const openDropdown = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropdownIsOpen(true);
  };
  const closeDropdown = () => setDropdownIsOpen(false);

  const setSelectedTagsForCategory = (
    tagIds: number[],
    category: string,
    prevTagCategoryFilterState: TagCategory[],
  ) => {
    const selectedTagsByCategoryLogicalId = _.keyBy(
      prevTagCategoryFilterState,
      (category) => category.logicalId,
    );
    const prevStateSelectedCategory = selectedTagsByCategoryLogicalId[category];

    const tagsToSelect = tagIds.map((tagId) => {
      const foundTag = shelfLifeDisplayTagCategory.tags.find(
        (displayTag) => displayTag.id === tagId,
      );
      if (!foundTag) {
        throw new Error(
          `Failed to find tag in display tag list when selecting. Tag ID: ${tagId}`,
        );
      }
      return foundTag;
    });
    const updatedSelectedTags = {
      ...selectedTagsByCategoryLogicalId,
      [category]: {
        ...prevStateSelectedCategory,
        tags: tagsToSelect,
      },
    };
    return Object.values(updatedSelectedTags);
  };

  const toggleShelfLifeTag = (tagId: number) => {
    setSelectedFilters((prevState) => {
      return toggleTag({
        displayFilters,
        currentFilters: prevState,
        tagId,
      });
    });
  };

  const resetShelfLifeTags = () => {
    setSelectedFilters((prevState) => ({
      ...prevState,
      tagCategories: setSelectedTagsForCategory(
        [],
        SHELF_LIFE_TAG_CATEGORY_LOGICAL_ID,
        prevState.tagCategories || [],
      ),
    }));
  };

  const label = 'Use by';

  return (
    <React.Fragment>
      <FormControl sx={{ width: 'auto' }}>
        <StyledFilledSelect
          labelId="shelf-life-select-label"
          displayEmpty
          id="shelf-life-select"
          multiple
          // When on mobile, prevent the Select from opening its Menu and use Drawer instead
          open={!isSmallScreen && dropdownIsOpen}
          onOpen={openDropdown}
          onClose={closeDropdown}
          value={currentSelectedIds}
          label={label}
          renderValue={() => {
            if (!selectedShelfLifeTagCategory?.tags?.length) {
              return <>{label}</>;
            }

            if (selectedShelfLifeTagCategory?.tags?.length === 1) {
              return (
                <div css={{ color: theme.palette.primary.contrastText }}>
                  {selectedShelfLifeTagCategory.tags[0].displayName}
                </div>
              );
            }

            return (
              <div css={{ display: 'flex' }}>
                <div
                  css={{
                    color: theme.palette.primary.contrastText,
                    marginRight: '4px',
                  }}
                >
                  {selectedShelfLifeTagCategory.tags[0].displayName}
                </div>
                <div css={{ color: theme.palette.secondary.contrastText }}>
                  + {selectedShelfLifeTagCategory.tags.length - 1} more
                </div>
              </div>
            );
          }}
        >
          {/* Desktop/tablet dropdown items (unchanged) */}
          {shelfLifeDisplayTagCategory.tags.map((tag) => (
            <MenuItem
              key={tag.id}
              disableRipple
              value={tag.id}
              onClick={() => toggleShelfLifeTag(tag.id)}
              sx={{
                padding: '4px 9px',
                '&.Mui-selected, &.Mui-selected.Mui-focusVisible': {
                  backgroundColor: 'transparent',
                },
                '&.Mui-selected:hover, &.Mui-selected:hover.Mui-selected.Mui-focusVisible':
                  {
                    backgroundColor: theme.palette.action.hover,
                  },
              }}
            >
              <Checkbox
                sx={{ paddingLeft: 0 }}
                checked={
                  selectedShelfLifeTagCategory?.tags
                    .map((selectedTag) => selectedTag.id)
                    .includes(tag.id) || false
                }
              />
              <ListItemText
                primary={tag.displayName}
                secondary={tag.description}
                slotProps={{ primary: { sx: { fontSize: '0.875rem' } } }}
              />
            </MenuItem>
          ))}
          <div css={{ padding: '9px' }}>
            <Button variant="outlined" onClick={resetShelfLifeTags}>
              Reset
            </Button>
          </div>
        </StyledFilledSelect>
      </FormControl>

      {/* Mobile Drawer: acts as the Select menu on small screens */}
      <Drawer
        anchor="bottom"
        open={isSmallScreen && dropdownIsOpen}
        onClose={closeDropdown}
        ModalProps={{ keepMounted: true }}
        css={{
          zIndex: 2000,
        }}
        slotProps={{
          paper: {
            style: { height: 'auto' },
            sx: {
              borderTopLeftRadius: 10,
              borderTopRightRadius: 10,
            },
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ px: 1, py: 0.5 }}>
          <List disablePadding>
            {shelfLifeDisplayTagCategory.tags.map((tag) => {
              const checked =
                selectedShelfLifeTagCategory?.tags
                  .map((selectedTag) => selectedTag.id)
                  .includes(tag.id) || false;
              return (
                <ListItemButton
                  key={tag.id}
                  onClick={() => toggleShelfLifeTag(tag.id)}
                  dense
                  sx={{ py: 0.5 }}
                >
                  <Checkbox edge="start" checked={checked} />
                  <ListItemText
                    primary={tag.displayName}
                    secondary={tag.description}
                    slotProps={{ primary: { sx: { fontSize: '0.875rem' } } }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
        <Divider />
        <Box
          sx={{
            p: 2,
            display: 'flex',
            gap: 1,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Button variant="outlined" onClick={resetShelfLifeTags}>
            Reset
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" onClick={closeDropdown}>
              Done
            </Button>
          </Box>
        </Box>
      </Drawer>
    </React.Fragment>
  );
};

export default ShelfLifeFilter;
